import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

const navLinks = [
  { label: 'SHOP', href: '#products' },
  { label: 'COLLECTIONS', href: '#collections' },
  { label: 'ABOUT', href: '#about' },
  { label: 'CONTACT', href: '#contact' },
]

// Hamburger menu icon
function HamburgerIcon({ isOpen, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative w-10 h-10 flex flex-col justify-center items-center gap-1.5 lg:hidden"
      aria-label="Toggle menu"
    >
      <motion.span
        className="w-5 h-0.5 bg-white rounded-full"
        animate={{
          rotate: isOpen ? 45 : 0,
          y: isOpen ? 4 : 0,
        }}
        transition={{ duration: 0.2 }}
      />
      <motion.span
        className="w-5 h-0.5 bg-white rounded-full"
        animate={{
          opacity: isOpen ? 0 : 1,
          scale: isOpen ? 0 : 1,
        }}
        transition={{ duration: 0.2 }}
      />
      <motion.span
        className="w-5 h-0.5 bg-white rounded-full"
        animate={{
          rotate: isOpen ? -45 : 0,
          y: isOpen ? -4 : 0,
        }}
        transition={{ duration: 0.2 }}
      />
    </button>
  )
}

// Search icon
function SearchIcon() {
  return (
    <button
      className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity"
      aria-label="Search"
    >
      <svg
        className="w-5 h-5 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </button>
  )
}

// User profile icon
function UserIcon() {
  return (
    <button
      className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity"
      aria-label="User account"
    >
      <svg
        className="w-5 h-5 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    </button>
  )
}

// Cart icon
function CartIcon() {
  return (
    <Link
      to="/cart"
      className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity relative"
      aria-label="Shopping cart"
    >
      <svg
        className="w-5 h-5 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
        />
      </svg>
      {/* Cart badge */}
      <span className="absolute top-1 right-1 w-4 h-4 bg-white text-[#1A1A1A] text-[10px] font-medium rounded-full flex items-center justify-center">
        2
      </span>
    </Link>
  )
}

// Navigation link
function NavLink({ href, children }) {
  return (
    <a
      href={href}
      className={cn(
        'relative px-4 py-2 text-xs font-body text-white/90',
        'uppercase tracking-[0.15em]',
        'hover:text-white transition-colors duration-200',
        'after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2',
        'after:w-0 after:h-px after:bg-white',
        'after:transition-all after:duration-300',
        'hover:after:w-full'
      )}
    >
      {children}
    </a>
  )
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <>
      {/* Main Header - Solid Black - Full Width */}
      <header className="fixed top-0 left-0 w-full z-50 bg-[#1A1A1A]">
        <div className="flex items-center justify-between w-full px-6 lg:px-12 h-16 lg:h-20">
          {/* Left section: Hamburger (mobile) + Nav (desktop) */}
          <div className="flex items-center gap-8">
            <HamburgerIcon
              isOpen={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            />

            {/* Desktop navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <NavLink key={link.label} href={link.href}>
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Center: Logo */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 flex items-center"
          >
            <span className="font-display font-bold text-white tracking-wide text-lg lg:text-xl">
              Nakhoons
            </span>
          </Link>

          {/* Right section: Icons */}
          <div className="flex items-center gap-1">
            <SearchIcon />
            <UserIcon />
            <CartIcon />
          </div>
        </div>
      </header>

      {/* Full-screen mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-[#1A1A1A]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu content */}
            <motion.div
              className="relative h-full flex flex-col items-center justify-center gap-8 p-8 pt-24"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.1 }}
            >
              {navLinks.map((link, idx) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  className="text-2xl font-display font-medium text-white uppercase tracking-[0.2em] hover:text-white/70 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.08 }}
                >
                  {link.label}
                </motion.a>
              ))}

              {/* CTA in mobile menu */}
              <motion.a
                href="#products"
                className={cn(
                  'mt-8 px-8 py-3',
                  'bg-white text-[#1A1A1A] text-sm font-medium',
                  'uppercase tracking-wider',
                  'hover:bg-white/90 transition-colors'
                )}
                onClick={() => setIsMenuOpen(false)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Shop Now
              </motion.a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <div className="h-16 lg:h-20" />
    </>
  )
}
